import { useState } from "react";
import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/fingerprint";
import { ShieldCheck, Loader2, Gift, ArrowLeft } from "lucide-react";

export default function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("login"); // login | invite
  const [code, setCode] = useState("");
  const [invite, setInvite] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const deviceId = await getDeviceId();
      const r = await api.post("/auth/login", { code: code.trim().toUpperCase(), device_id: deviceId });
      onLogin(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di connessione");
    } finally {
      setBusy(false);
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const deviceId = await getDeviceId();
      const r = await api.post("/auth/referral", { ref_code: invite.trim().toUpperCase(), device_id: deviceId });
      onLogin(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di connessione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md fade-up">
        <div className="flex flex-col items-center mb-4">
          <img src="/logo.png" alt="Zero Signals" data-testid="login-logo" className="h-16 sm:h-20 w-auto object-contain" />
        </div>

        <div className="bg-black border border-amber-500/20 gold-glow rounded-md p-8">
          {mode === "login" ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-amber-400" />
                <h2 className="font-heading font-semibold text-base text-amber-100/90 uppercase tracking-wide">Accesso Terminale</h2>
              </div>
              <p className="text-sm text-slate-400 mb-6">Inserire il codice identificativo personale (formato ZSXXXXXX)</p>

              <form onSubmit={submit} className="space-y-4">
                <input
                  data-testid="login-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  placeholder="ZSXXXXXX"
                  autoFocus
                  className="w-full bg-black border border-amber-500/25 rounded-sm px-4 py-3 font-mono-data text-xl tracking-[0.35em] text-center text-amber-300 placeholder:text-slate-700 focus:outline-none focus:border-amber-400 transition-colors"
                />
                {error && (
                  <p data-testid="login-error-message" role="alert" className="text-sm text-red-400 font-mono-data text-center">
                    {error}
                  </p>
                )}
                <button
                  data-testid="login-submit-button"
                  disabled={busy || code.length !== 8}
                  className="w-full py-3 rounded-sm bg-gradient-to-r from-[#D4AF37] to-[#B8952E] text-black font-heading font-bold uppercase tracking-widest text-sm disabled:opacity-40 hover:from-[#E5C158] hover:to-[#D4AF37] transition-colors flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {busy ? "Verifica…" : "Verifica Codice e Connetti"}
                </button>
              </form>

              <button
                data-testid="login-have-invite-button"
                onClick={() => { setMode("invite"); setError(""); }}
                className="mt-5 w-full flex items-center justify-center gap-1.5 text-xs font-heading uppercase tracking-widest text-[#E3BC55]/80 hover:text-[#FFD700] transition-colors"
              >
                <Gift className="w-3.5 h-3.5" /> Ho un invito
              </button>

              <p data-testid="login-get-code-cta" className="mt-4 text-center text-xs text-slate-500">
                Non hai il codice?{" "}
                <a
                  href="https://www.zerosignals.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-heading uppercase tracking-wider text-[#E3BC55] hover:text-[#FFD700] underline underline-offset-2 transition-colors"
                >
                  Ottienilo su zerosignals.it
                </a>
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Gift className="w-4 h-4 text-amber-400" />
                <h2 className="font-heading font-semibold text-base text-amber-100/90 uppercase tracking-wide">Riscatta Invito</h2>
              </div>
              <p className="text-sm text-slate-400 mb-6">Inserisci il codice invito ricevuto da un amico e ricevi <span className="text-[#E3BC55] font-semibold">3 giorni gratis</span>.</p>

              <form onSubmit={redeem} className="space-y-4">
                <input
                  data-testid="invite-code-input"
                  value={invite}
                  onChange={(e) => setInvite(e.target.value.toUpperCase())}
                  maxLength={8}
                  placeholder="ZSXXXXXX"
                  autoFocus
                  className="w-full bg-black border border-amber-500/25 rounded-sm px-4 py-3 font-mono-data text-xl tracking-[0.35em] text-center text-amber-300 placeholder:text-slate-700 focus:outline-none focus:border-amber-400 transition-colors"
                />
                {error && (
                  <p data-testid="invite-error-message" role="alert" className="text-sm text-red-400 font-mono-data text-center">
                    {error}
                  </p>
                )}
                <button
                  data-testid="invite-submit-button"
                  disabled={busy || invite.length !== 8}
                  className="w-full py-3 rounded-sm bg-gradient-to-r from-[#D4AF37] to-[#B8952E] text-black font-heading font-bold uppercase tracking-widest text-sm disabled:opacity-40 hover:from-[#E5C158] hover:to-[#D4AF37] transition-colors flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {busy ? "Attivazione…" : "Riscatta e Attiva"}
                </button>
              </form>

              <button
                data-testid="invite-back-button"
                onClick={() => { setMode("login"); setError(""); }}
                className="mt-5 w-full flex items-center justify-center gap-1.5 text-xs font-heading uppercase tracking-widest text-slate-400 hover:text-[#E3BC55] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Ho già un codice
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
